/**
 * Branded loading screen shown while auth / profile resolve (web + native iOS).
 *
 * The two chevrons of the app mark clash head-to-head inside the icon while a
 * green progress ring sweeps around it. Pure CSS — no JS animation loop, no
 * spinner from the design system's old splash. Self-contained: keyframes and the
 * gradient defs live here so the splash renders identically before the rest of
 * the app (and its styles) have mounted.
 */
export function SplashScreen() {
  return (
    <div
      className="relative flex h-full w-full flex-col items-center justify-center gap-10 px-10"
      style={{
        background:
          'radial-gradient(120% 46% at 50% 42%, rgba(36,197,106,0.10) 0%, rgba(255,255,255,0) 60%), #FFFFFF'
      }}
    >
      <style>{SPLASH_KEYFRAMES}</style>

      {/* shared gradient defs */}
      <svg width="0" height="0" style={{ position: 'absolute' }} aria-hidden="true">
        <defs>
          <linearGradient id="h2hGreen" x1="0" y1="0" x2="0.25" y2="1">
            <stop offset="0" stopColor="#3CE283" />
            <stop offset="1" stopColor="#1B9650" />
          </linearGradient>
          <linearGradient id="h2hWhite" x1="0" y1="0" x2="0.25" y2="1">
            <stop offset="0" stopColor="#FFFFFF" />
            <stop offset="1" stopColor="#E7E9EC" />
          </linearGradient>
          <linearGradient id="h2hRing" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0" stopColor="#2EDB7B" />
            <stop offset="1" stopColor="#0BA078" />
          </linearGradient>
        </defs>
      </svg>

      {/* progress ring + clashing chevrons */}
      <div style={{ position: 'relative', width: 200, height: 200, display: 'grid', placeItems: 'center' }}>
        <div
          style={{
            position: 'absolute',
            inset: 18,
            borderRadius: '50%',
            background: 'radial-gradient(circle, rgba(36,197,106,0.40), transparent 68%)',
            animation: 'h2hGlow 2.8s ease-in-out infinite',
            pointerEvents: 'none'
          }}
        />
        <svg width="200" height="200" viewBox="0 0 200 200" style={{ position: 'absolute', inset: 0 }}>
          <circle cx="100" cy="100" r="94" fill="none" stroke="#E4E5EB" strokeWidth="3.5" />
        </svg>
        <svg
          width="200"
          height="200"
          viewBox="0 0 200 200"
          style={{ position: 'absolute', inset: 0, animation: 'h2hSpin 1.3s linear infinite' }}
        >
          <circle
            cx="100"
            cy="100"
            r="94"
            fill="none"
            stroke="url(#h2hRing)"
            strokeWidth="3.5"
            strokeLinecap="round"
            strokeDasharray="150 440"
          />
        </svg>
        <div style={{ position: 'relative', width: 132, height: 132 }}>
          <div
            style={{
              position: 'absolute',
              inset: 0,
              borderRadius: 30,
              overflow: 'hidden',
              background:
                'radial-gradient(130% 120% at 32% 22%, #2a2e38 0%, #14171d 48%, #080a0e 100%)',
              boxShadow: '0 26px 50px -18px rgba(8,9,13,0.6), inset 0 1px 0 rgba(255,255,255,0.10)'
            }}
          >
            <div style={{ position: 'absolute', inset: 0, animation: 'h2hPop 3.6s ease-in-out infinite' }}>
              <svg
                viewBox="0 0 100 100"
                width="132"
                height="132"
                style={{
                  position: 'absolute',
                  inset: 0,
                  display: 'block',
                  animation: 'h2hGreenIn 3.6s cubic-bezier(0.16,0.84,0.3,1) infinite'
                }}
              >
                <path
                  d="M31 29 L46.5 51 L31 73.5"
                  fill="none"
                  stroke="url(#h2hGreen)"
                  strokeWidth="12.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              <svg
                viewBox="0 0 100 100"
                width="132"
                height="132"
                style={{
                  position: 'absolute',
                  inset: 0,
                  display: 'block',
                  animation: 'h2hWhiteIn 3.6s cubic-bezier(0.16,0.84,0.3,1) infinite'
                }}
              >
                <path
                  d="M70 28.5 L51 51 L70 73.5"
                  fill="none"
                  stroke="#0A0C10"
                  strokeWidth="15.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M70 28.5 L51 51 L70 73.5"
                  fill="none"
                  stroke="url(#h2hWhite)"
                  strokeWidth="12.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              <div style={{ position: 'absolute', inset: 0, borderRadius: 30, overflow: 'hidden', pointerEvents: 'none' }}>
                <div
                  style={{
                    position: 'absolute',
                    top: '-30%',
                    left: 0,
                    width: '38%',
                    height: '160%',
                    background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.32), transparent)',
                    animation: 'h2hShine 3.6s ease-in-out infinite'
                  }}
                />
              </div>
              <div
                style={{
                  position: 'absolute',
                  left: '50%',
                  top: '50%',
                  width: 92,
                  height: 92,
                  borderRadius: '50%',
                  background:
                    'radial-gradient(circle, rgba(255,255,255,0.92) 0%, rgba(36,197,106,0.5) 40%, transparent 68%)',
                  animation: 'h2hFlash 3.6s ease-in-out infinite',
                  pointerEvents: 'none'
                }}
              />
            </div>
          </div>
        </div>
      </div>

      {/* wordmark */}
      <div style={{ textAlign: 'center' }}>
        <div
          style={{
            fontFamily: "'Space Grotesk', -apple-system, sans-serif",
            fontSize: 27,
            fontWeight: 700,
            letterSpacing: '-0.03em',
            color: '#15151B'
          }}
        >
          HeadToHead
        </div>
        <div
          style={{
            fontFamily: "'Space Mono', monospace",
            fontSize: 10.5,
            fontWeight: 700,
            letterSpacing: '0.22em',
            color: '#0A9A72',
            marginTop: 8
          }}
        >
          GETTING THINGS READY
        </div>
      </div>
    </div>
  );
}

const SPLASH_KEYFRAMES = `
@keyframes h2hGreenIn {
  0%   { transform:translateX(-52px); opacity:0; }
  14%  { opacity:1; }
  32%,82% { transform:translateX(0); opacity:1; }
  92%  { transform:translateX(-14px); opacity:0; }
  100% { transform:translateX(-52px); opacity:0; }
}
@keyframes h2hWhiteIn {
  0%   { transform:translateX(52px); opacity:0; }
  14%  { opacity:1; }
  32%,82% { transform:translateX(0); opacity:1; }
  92%  { transform:translateX(14px); opacity:0; }
  100% { transform:translateX(52px); opacity:0; }
}
@keyframes h2hPop {
  0%,29% { transform:scale(1); }
  33%    { transform:scale(1.05); }
  40%,100% { transform:scale(1); }
}
@keyframes h2hFlash {
  0%,27% { opacity:0; transform:translate(-50%,-50%) scale(0.4); }
  33%    { opacity:0.7; transform:translate(-50%,-50%) scale(1); }
  50%    { opacity:0; transform:translate(-50%,-50%) scale(1.4); }
  100%   { opacity:0; transform:translate(-50%,-50%) scale(1.4); }
}
@keyframes h2hGlow { 0%,100% { opacity:0.4; transform:scale(1); } 50% { opacity:0.82; transform:scale(1.08); } }
@keyframes h2hShine { 0%,54% { transform:translateX(-190%) rotate(16deg); } 70%,100% { transform:translateX(300%) rotate(16deg); } }
@keyframes h2hSpin { from { transform:rotate(0deg); } to { transform:rotate(360deg); } }
`;
